import { useLayoutEffect, useRef } from "react";

/**
 * FLIP animation hook for list items. Animates items smoothly when their
 * order changes (e.g. from SSE rank updates or sort changes).
 *
 * Each item must have a `data-{dataAttribute}` attribute with a unique identifier.
 * Works with any container element (table body, div list, etc.).
 *
 * @param containerRef - Ref to the container element holding the items
 * @param dataAttribute - The data attribute name used to identify items (e.g. "campaign-id" reads `data-campaign-id`)
 * @param deps - Dependency array that triggers position recalculation
 */
export const useFlipAnimation = (
  containerRef: React.RefObject<HTMLElement | null>,
  dataAttribute: string,
  deps: React.DependencyList,
) => {
  const previousPositionsRef = useRef<Map<string, number>>(new Map());
  const previousIdsRef = useRef<string[]>([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const selector = `[data-${dataAttribute}]`;
    const rows = Array.from(container.querySelectorAll<HTMLElement>(selector));

    // Camel-case the attribute name for dataset access (e.g. "campaign-id" -> "campaignId")
    const datasetKey = dataAttribute.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

    const currentIds = rows.map((row) => row.dataset[datasetKey]).filter((id): id is string => Boolean(id));

    const currentPositions = new Map<string, number>();
    rows.forEach((row) => {
      const id = row.dataset[datasetKey];
      if (id) {
        currentPositions.set(id, row.getBoundingClientRect().top);
      }
    });

    const previousIds = previousIdsRef.current;
    const hasSameRows = previousIds.length === currentIds.length && previousIds.every((id) => currentPositions.has(id));
    const orderChanged = hasSameRows && previousIds.some((id, index) => id !== currentIds[index]);

    if (orderChanged) {
      rows.forEach((row) => {
        const id = row.dataset[datasetKey];
        if (!id) return;

        const previousTop = previousPositionsRef.current.get(id);
        const currentTop = currentPositions.get(id);
        if (previousTop == null || currentTop == null) return;

        const deltaY = previousTop - currentTop;
        if (Math.abs(deltaY) < 1) return;

        row.animate([{ transform: `translateY(${deltaY}px)` }, { transform: "translateY(0)" }], {
          duration: 260,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        });
      });
    }

    previousPositionsRef.current = currentPositions;
    previousIdsRef.current = currentIds;
  }, deps);
};
